import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Trans, useTranslation } from 'react-i18next';

import {
    Box,
    Typography,
    Stack,
} from '@mui/material';
import {
    DataGrid,
    GRID_STRING_COL_DEF,
} from '@mui/x-data-grid';
import {
    DatePicker,
    LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
    ArrowRightAltOutlined,
    SubdirectoryArrowLeftRounded
} from '@mui/icons-material';

import { de } from 'date-fns/locale/de';
import { enUS } from 'date-fns/locale/en-US'
import SearchBar from '../shared/SearchBar';
import StatusChip from './StatusChip';
import EditStatusActionItem from './EditStatusActionItem';
import api from '../../api';
import UnsubmitDialog from './UnsubmitDialog';
import useLocalStorage from '../../utils/useLocalStorage';

const StatusChipCell = (props) => {
    if (props.value == null) {
        return null;
    };

    return (
        <Stack direction="row" spacing={.5}>
            <StatusChip status={props.row.submitted} />
        </Stack>
    );
};

const chipColumnType = {
    ...GRID_STRING_COL_DEF,
    resizable: false,
    filterable: false,
    sortable: false,
    editable: false,
    groupable: false,
    renderCell: (params) => <StatusChipCell {...params} />,
};

function StatusOverviewPage() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [users, setUsers] = useState([]);
    const [submittedStatus, setSubmittedStatuses] = useState([]);
    const [rows, setRows] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState({ month: new Date(), user: { id: '', name: '' } });
    const [urlParam, setUrlParam] = useSearchParams({ 'search': '' });
    const [filter, setFilter] = useState(urlParam.get('search') || '');
    const [open, setOpen] = useState(false);
    const language = useLocalStorage('language', 'de')[0];

    const { t } = useTranslation();

    useEffect(() => {
        let s = null
        let e = null

        if(startDate){
            s = startDate
            s.setDate(1)
        }
        // Gets the last day of the month, since setDate(0) takes the last day of the previous month
        if(endDate){
            e = endDate
            e.setMonth(e.getMonth() +1)
            e.setDate(0)
        }

        api.readWorkRecordsSubmittedStatus(users.map(u => u.id), s, e).then(res => {
            setSubmittedStatuses(res);
        });
    }, [users, startDate, endDate]);

    useEffect(() => {
        api.readUsers().then(res => {
            setUsers(res);
        });
    }, []);


    useEffect(() => {

        let rows = submittedStatus.map(ss => {
            return {
                id: ss.user.id + ss.month,
                user: ss.user,
                month: ss.month,
                submitted: ss.submitted,
            };
        }).filter(row => {
            return row.user.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase());
        });

        setRows(rows);
    }, [submittedStatus, filter]);

    const handleOnFilter = (e) => {
        setFilter(e.target.value);
        setUrlParam({ 'search': e.target.value });
    };

    const handleOnClear = () => {
        setFilter('');
        setUrlParam('');
    };

    const handleUnsubmitMonth = (userId, month) => {
        api.unsubmitMonths(userId, [month.toISOString().split('T')[0]]).then(res => {
            api.readWorkRecordsSubmittedStatus(users.map(u => u.id), startDate, endDate).then(res => {
                setSubmittedStatuses(res);
            });
        });
    };

    const handleOpenDialog = (submitStatus) => {
        setSelectedStatus(submitStatus);
        setOpen(true);
    }

    const columns = [
        {
            field: 'month',
            headerName: t('monat', { ns: 'statusOverviewPage', defaultValue: 'Monat' }),
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell : (params) => params.row.month.toLocaleDateString(language === 'de' ? 'de-De' : 'en-US', { month: 'long', year: 'numeric' })
        },
        {
            field: 'user',
            headerName: 'Name',
            flex: 1, renderCell: (params) => params.row.user.name,
            sortable: false,
            filterable: false,
        },
        {
            field: 'submitted',
            headerName: 'Status',
            flex: 1,
            ...chipColumnType,
        },
        {
            field: 'actions',
            headerName: t('aktion', { ns: 'statusOverviewPage', defaultValue: 'Aktion' }),
            flex: 0,
            type: 'actions',
            getActions: (params) => {
                return [
                    <EditStatusActionItem
                        label={t('freigabeAufheben', { ns: 'statusOverviewPage', defaultValue: 'Freigabe aufheben' })}
                        disabled={!params.row.submitted}
                        handleSubmit={() => handleOpenDialog(params.row)}
                        icon={<SubdirectoryArrowLeftRounded />}
                    />
                ];
            },
        },
    ];

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'de' ? de : enUS}>
                <Typography variant='overline' gutterBottom><Trans i18nKey='periode' ns='statusOverviewPage'>Periode</Trans></Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', mb: 1 }}>
                    <DatePicker label={t('von', { ns: 'statusOverviewPage', defaultValue: 'Von' })} views={['year', 'month']} value={startDate} onChange={(val) => setStartDate(val)} />
                    <ArrowRightAltOutlined />
                    <DatePicker label={t('bis', { ns: 'statusOverviewPage', defaultValue: 'Bis' })} views={['year', 'month']} value={endDate} onChange={(val) => setEndDate(val)} minDate={startDate} />
                </Box>
                <Typography variant='overline' gutterBottom><Trans i18nKey='mitarbeiter' ns='statusOverviewPage'>Mitarbeiter</Trans></Typography>
                <SearchBar
                    value={filter}
                    onChange={handleOnFilter}
                    onClear={handleOnClear}
                />
                <DataGrid
                    columns={columns}
                    rows={rows}
                    disableColumnMenu
                    disableColumnFilter
                    sx={{ mt: 5 }}
                />
                <UnsubmitDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    status={selectedStatus}
                    onSubmit={handleUnsubmitMonth}
                />
            </LocalizationProvider>
        </>
    );
}

export default StatusOverviewPage
